// Strict lazy loader for deferred media sources.
(function () {
  const params = new URLSearchParams(window.location.search);
  const debugEnabled =
    params.get('lazyDebug') === '1' ||
    window.localStorage.getItem('lazyDebug') === '1';

  function debugLog(message, payload) {
    if (!debugEnabled) return;
    if (payload !== undefined) {
      console.log('[lazy-debug]', message, payload);
    } else {
      console.log('[lazy-debug]', message);
    }
  }

  function targetLabel(el) {
    if (!el) return 'unknown';
    const src = el.getAttribute && (el.getAttribute('data-src') || el.getAttribute('src'));
    if (src) return src;
    const tag = el.tagName ? el.tagName.toLowerCase() : 'node';
    return tag;
  }

  function activatePicture(picture) {
    if (!picture) return;
    debugLog('activate picture', targetLabel(picture));

    const deferredSources = picture.querySelectorAll('source[data-srcset]');
    deferredSources.forEach((source) => {
      debugLog('activate source', source.getAttribute('data-srcset'));
      source.setAttribute('srcset', source.getAttribute('data-srcset'));
      source.removeAttribute('data-srcset');
    });

    const deferredImg = picture.querySelector('img[data-src]');
    if (deferredImg) {
      debugLog('activate img in picture', deferredImg.getAttribute('data-src'));
      deferredImg.classList.add('lazy-loading');
      deferredImg.addEventListener(
        'load',
        () => {
          deferredImg.classList.remove('lazy-loading');
          deferredImg.classList.add('lazy-loaded');
        },
        { once: true }
      );
      deferredImg.setAttribute('src', deferredImg.getAttribute('data-src'));
      deferredImg.removeAttribute('data-src');
      deferredImg.classList.remove('lazy-defer');
    }
  }

  function activateImage(img) {
    if (!img || !img.getAttribute('data-src')) return;
    debugLog('activate img', img.getAttribute('data-src'));
    img.classList.add('lazy-loading');
    img.addEventListener(
      'load',
      () => {
        img.classList.remove('lazy-loading');
        img.classList.add('lazy-loaded');
      },
      { once: true }
    );
    img.setAttribute('src', img.getAttribute('data-src'));
    img.removeAttribute('data-src');
    img.classList.remove('lazy-defer');
  }

  function activateTarget(target) {
    if (target.tagName === 'PICTURE') {
      activatePicture(target);
      return;
    }

    if (target.tagName === 'IMG') {
      const picture = target.closest('picture');
      if (picture && picture.querySelector('source[data-srcset]')) {
        activatePicture(picture);
      } else {
        activateImage(target);
      }
    }
  }

  function initStrictLazyLoading() {
    const seen = new WeakSet();
    let observer = null;

    function isLikelyLcpTarget(target) {
      if (!target || typeof target.getBoundingClientRect !== 'function') return false;
      if (target.hasAttribute && target.hasAttribute('data-force-lazy')) return false;

      const img = target.tagName === 'IMG' ? target : target.querySelector && target.querySelector('img');
      if (!img) return false;
      if (img.hasAttribute('data-lcp') || img.classList.contains('lcp-image')) return true;
      if (img.getAttribute('fetchpriority') === 'high') return true;

      const rect = img.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      if (!vh || !vw) return false;

      const largeEnough = (rect.width * rect.height) > 50000;
      const inOrNearViewport = rect.top < vh * 1.2 && rect.bottom > 0 && rect.left < vw && rect.right > 0;
      return largeEnough && inOrNearViewport;
    }

    function collectDeferredTargets(scope) {
      const targets = [];
      scope.querySelectorAll('img[data-src]').forEach((img) => {
        img.classList.add('lazy-defer');
        targets.push(img);
      });
      scope.querySelectorAll('picture').forEach((picture) => {
        if (picture.querySelector('source[data-srcset]')) {
          targets.push(picture);
        }
      });
      return targets;
    }

    function observeTargets(scope) {
      const targets = collectDeferredTargets(scope);
      targets.forEach((target) => {
        if (seen.has(target)) return;
        seen.add(target);

        if (isLikelyLcpTarget(target)) {
          debugLog('activate early lcp target', targetLabel(target));
          activateTarget(target);
          return;
        }

        if (observer) {
          debugLog('observe target', targetLabel(target));
          observer.observe(target);
        } else {
          activateTarget(target);
        }
      });
      return targets.length;
    }

    const totalTargets = collectDeferredTargets(document).length;
    debugLog('init strict lazy loading');
    debugLog('total deferred targets', totalTargets);
    if (!totalTargets) return;

    if (!('IntersectionObserver' in window)) {
      debugLog('IntersectionObserver not supported; activate all immediately');
      observeTargets(document);
      return;
    }

    observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          debugLog('observer entry', {
            target: targetLabel(entry.target),
            isIntersecting: entry.isIntersecting,
            ratio: entry.intersectionRatio,
          });
          if (!entry.isIntersecting) return;
          activateTarget(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px', threshold: 0.01 }
    );

    observeTargets(document);

    // Slider libraries create cloned slides dynamically; observe new nodes too.
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          observeTargets(node);
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener('DOMContentLoaded', initStrictLazyLoading);
})();
